import React from 'react';
import PropTypes from 'prop-types';
import { Card, Table, Tooltip, Typography } from 'antd';
import styled from 'styled-components';
import { HALT_META, STOPPAGE_BAR, TABLE_HEADER_BG } from './constants';
import { formatCoordinates, formatDuration, formatTimestamp } from './format';

const { Title, Text } = Typography;

const TableWrapper = styled.div`
	margin-top: 20px;

	.ant-table-thead > tr > th {
		background-color: ${TABLE_HEADER_BG} !important;
		color: #ffffff;
		border-bottom: none;
		font-weight: 600;
		/* The header is its own element once scroll.y is on; keep the corners rounded
		   there too or the dark bar grows square edges only when the table scrolls. */
		white-space: nowrap;
	}
	.ant-table-tbody > tr > td {
		border-bottom: 1px solid #f1f5f9;
		/* A wrapped two-line address must not centre itself against one-line cells. */
		vertical-align: top;
	}
	/* Long place names wrap instead of stretching the table. overflow-wrap covers the
	   pathological case - a single unbroken token with nowhere to break. */
	.stoppage-location-cell {
		white-space: normal;
		overflow-wrap: anywhere;
		line-height: 1.45;
	}
	/* Scrolling body: keep the dark header pinned and give the scrollbar room. */
	.ant-table-body {
		scrollbar-width: thin;
	}
`;

// Beyond this many rows the body scrolls rather than growing the page. A trip with
// dozens of traffic halts should not push the Odometer card off-screen.
const SCROLL_AFTER_ROWS = 6;
const TABLE_BODY_MAX_HEIGHT = 340;

/**
 * Where the trip's hours actually went. Total duration on its own hides every delay inside it.
 *
 * The bar's `Unmeasured` band is the backend's own `total_untracked_gap_minutes` — time no
 * source pinged for at all, which is neither moving nor stopped but genuinely unknown.
 * Folding it into moving time would be the easy lie and the exact failure this tab exists
 * to prevent.
 */
function StoppageBreakdown({ model }) {
	const { stoppages } = model;
	const spansDays = (model.window.totalSeconds || 0) > 86400;
	const totalSeconds = stoppages.totalSeconds || 0;

	// Doubles as the bar's legend and as the PRD's stoppage metric table.
	const metrics = [
		{ key: 'moving', seconds: stoppages.movingSeconds },
		{ key: 'dwell', seconds: stoppages.dwellSeconds },
		{ key: 'halt', seconds: stoppages.inTransitSeconds, label: 'In-Transit Halt' },
		{ key: 'unscheduled', seconds: stoppages.unscheduledSeconds, label: 'Unscheduled Long' },
		{ key: 'unmeasured', seconds: stoppages.untrackedSeconds, label: 'Untracked (no GPS)' },
	].filter((metric) => metric.seconds !== null);

	const columns = [
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			width: 180,
			render: (type, record) => {
				const label = HALT_META[type]?.label || type;
				const text = (
					<Text style={{ fontSize: 13, color: '#0f172a' }}>
						{record.nodeSequence ? `${label} (Node ${record.nodeSequence})` : label}
					</Text>
				);
				return record.fromNodeTimestamps ? (
					<Tooltip title="Derived from this node's check-in and check-out times, not from a GPS trail.">
						<span style={{ borderBottom: '1px dotted #94a3b8', cursor: 'help' }}>
							{text}
						</span>
					</Tooltip>
				) : (
					text
				);
			},
		},
		{
			title: 'Location',
			key: 'location',
			// No width: this is the flexible column, so it absorbs whatever the fixed
			// ones leave. A reverse-geocoded address runs long ("Delhi - Dehradun Expy,
			// Shikohpur, Uttar Pradesh 250611, India") and wraps rather than truncating -
			// the district and state are the parts that tell Ops where the truck was.
			render: (_, record) => {
				// Three tiers of certainty, best first: our own node, then whatever the
				// geocoder recognised at those coordinates, then the raw coordinates. The
				// last is not a failure - it is the honest label when nothing can name it.
				const label =
					record.nodeName ||
					record.locationName ||
					formatCoordinates(record.lat, record.lng);
				return <div className="stoppage-location-cell">{label}</div>;
			},
		},
		{
			title: 'Start',
			dataIndex: 'start',
			key: 'start',
			width: 120,
			render: (value) => formatTimestamp(value, spansDays),
		},
		{
			title: 'Duration',
			dataIndex: 'durationSeconds',
			key: 'duration',
			width: 110,
			align: 'right',
			render: (value) => formatDuration(value),
		},
		{
			title: 'Flag',
			key: 'flag',
			width: 140,
			render: (_, record) => {
				const meta = HALT_META[record.type];
				if (!meta) return null;
				const pill = (
					<span
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 7,
							background: meta.flagBackground,
							color: meta.flagColor,
							borderRadius: 999,
							padding: '3px 12px',
							fontSize: 12,
							fontWeight: 600,
						}}
					>
						<span
							style={{
								width: 7,
								height: 7,
								borderRadius: '50%',
								background: meta.flagColor,
							}}
						/>
						{meta.flag}
					</span>
				);
				// The device reported Halted/Idling itself rather than us inferring the stop
				// from position deltas — evidence, not inference, and worth saying so.
				return record.sourceConfirmed ? (
					<Tooltip title="The tracking device reported this stop itself, rather than it being inferred from position.">
						{pill}
					</Tooltip>
				) : (
					pill
				);
			},
		},
	];

	// y is only set past a handful of rows: antd fixes the body height when it is
	// present, so a two-row table would otherwise render inside a mostly empty 340px
	// box. x is a MIN width - below it the table scrolls sideways rather than crushing
	// the Flag pills onto three lines.
	const tableScroll = {
		x: 760,
		y: stoppages.halts.length > SCROLL_AFTER_ROWS ? TABLE_BODY_MAX_HEIGHT : undefined,
	};

	return (
		<Card
			bodyStyle={{ padding: '24px 28px' }}
			style={{
				borderRadius: 14,
				border: 'none',
				boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
				marginTop: 20,
			}}
		>
			<Title level={5} style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>
				Stoppage Breakdown
			</Title>
			<Text style={{ display: 'block', marginTop: 8, fontSize: 13, color: '#64748b' }}>
				Total Trip Duration: {formatDuration(totalSeconds)}
				{model.window.endIsNow && ' (trip still open)'}
			</Text>

			{stoppages.bar.length > 0 && (
				<div
					style={{
						display: 'flex',
						height: 34,
						borderRadius: 8,
						overflow: 'hidden',
						marginTop: 16,
					}}
				>
					{stoppages.bar.map((segment) => {
						const meta = STOPPAGE_BAR[segment.key];
						const share = (segment.seconds / totalSeconds) * 100;
						return (
							<div
								key={segment.key}
								title={`${meta.label} · ${formatDuration(segment.seconds)}`}
								style={{
									flex: `0 0 ${Math.max(share, 4)}%`,
									background: meta.color,
									color: segment.key === 'unmeasured' ? '#475569' : '#ffffff',
									fontSize: 12,
									fontWeight: 600,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									overflow: 'hidden',
									whiteSpace: 'nowrap',
								}}
							>
								{share >= 10 &&
									`${meta.label} · ${formatDuration(segment.seconds)}`}
							</div>
						);
					})}
				</div>
			)}

			{metrics.length > 0 && (
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: '10px 28px',
						marginTop: 14,
					}}
				>
					{metrics.map((metric) => (
						<Text key={metric.key} style={{ fontSize: 13, color: '#64748b' }}>
							<span
								style={{
									display: 'inline-block',
									width: 9,
									height: 9,
									borderRadius: '50%',
									background: STOPPAGE_BAR[metric.key].color,
									marginRight: 7,
								}}
							/>
							<span>
								{metric.label || STOPPAGE_BAR[metric.key].label}:{' '}
								{formatDuration(metric.seconds)}
							</span>
						</Text>
					))}
				</div>
			)}

			{stoppages.halts.length > 0 ? (
				<TableWrapper>
					<Table
						size="middle"
						rowKey="key"
						pagination={false}
						columns={columns}
						dataSource={stoppages.halts}
						tableLayout="fixed"
						scroll={tableScroll}
					/>
				</TableWrapper>
			) : (
				<Text style={{ display: 'block', marginTop: 20, fontSize: 13, color: '#94a3b8' }}>
					No halts recorded for this trip.
				</Text>
			)}

			{stoppages.nodeFallback && (
				<Text style={{ display: 'block', marginTop: 14, fontSize: 12, color: '#94a3b8' }}>
					No GPS reached this trip, so dwell times below are derived from node check-in
					and check-out timestamps. Time between nodes cannot be attributed from those
					alone and stays under Untracked.
				</Text>
			)}

			{stoppages.partial && (
				<Text style={{ display: 'block', marginTop: 14, fontSize: 12, color: '#94a3b8' }}>
					{stoppages.inTransitSeconds
						? `In-transit halts total ${formatDuration(
								stoppages.inTransitSeconds
						  )} but are not itemised — the API returns individual rows for unscheduled long halts only.`
						: 'Only stops at route nodes can be itemised until continuous GPS ping history exists for this trip.'}
				</Text>
			)}
		</Card>
	);
}

StoppageBreakdown.propTypes = {
	model: PropTypes.object.isRequired,
};

export default StoppageBreakdown;
